import { Api } from 'lib';
import { useState } from 'react';
import TeamPicker from 'screens/Admin/screens/QuickQuiz/components/TeamPicker';
import { GOAL_CHANCES, SCORING_TEAMS } from '../labState';
import { Btn, Card, controlClass, Label } from '../ui';

const CreateGamePanel = ({ onCreated }) => {
  const [home, setHome] = useState(null);
  const [away, setAway] = useState(null);
  const [startMode, setStartMode] = useState('soon');
  const [startMinutes, setStartMinutes] = useState('3');
  const [goalChance, setGoalChance] = useState(0.15);
  const [scoringTeam, setScoringTeam] = useState('random');
  const [showStatistics, setShowStatistics] = useState(true);
  const [forcePenaltyShootout, setForcePenaltyShootout] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const minutes = Math.round(Number(startMinutes));
  const minutesValid =
    Number.isFinite(minutes) &&
    (startMode === 'soon' ? minutes >= 0 && minutes <= 1440 : minutes >= 0 && minutes <= 90);
  const canCreate = home && away && home._id !== away._id && minutesValid && !busy;

  async function create() {
    setBusy(true);
    setError('');
    const res = await Api.post(
      '/admin/mock/createMockGames',
      {
        teamIds: [home._id, away._id],
        // The endpoint counts match minutes back from now: positive is before kickoff.
        minutesFromNow: startMode === 'soon' ? minutes : -minutes,
        mockConfig: { goalChance, scoringTeam, showStatistics },
        forcePenaltyShootout
      },
      null,
      { silent: true }
    );
    setBusy(false);
    if (!res?.gameId) return setError(res?.err || 'The game could not be created.');
    onCreated(res.gameId);
  }

  return (
    <Card
      title="New mock game"
      subtitle="Comes with lineups, predictions and a POTM poll. Recreating a pair resets it."
    >
      <div className="flex flex-col gap-3">
        <TeamPicker label="Home" team={home} onChange={setHome} />
        <TeamPicker label="Away" team={away} onChange={setAway} />
        {home && away && home._id === away._id && (
          <p className="text-[12px] text-[#B91C1C]">Pick two different teams.</p>
        )}

        <div>
          <Label htmlFor="lab-start">Start</Label>
          <div className="flex items-center gap-2">
            <select
              id="lab-start"
              className={controlClass}
              value={startMode}
              onChange={(e) => setStartMode(e.target.value)}
            >
              <option value="soon">Kicks off in (min)</option>
              <option value="live">Already at minute</option>
            </select>
            <input
              aria-label={startMode === 'soon' ? 'Minutes to kickoff' : 'Match minute'}
              className={`${controlClass} w-20`}
              inputMode="numeric"
              value={startMinutes}
              onChange={(e) => setStartMinutes(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </div>
          {!minutesValid && (
            <p className="text-[12px] text-[#B91C1C] mt-1">
              {startMode === 'soon' ? 'Up to 1440 minutes ahead.' : 'A match minute is 0–90.'}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="lab-chance">Random goals</Label>
            <select
              id="lab-chance"
              className={`${controlClass} w-full`}
              value={goalChance}
              onChange={(e) => setGoalChance(Number(e.target.value))}
            >
              {GOAL_CHANCES.map((c) => (
                <option key={c.chance} value={c.chance}>
                  {c.value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="lab-scorer">Who scores</Label>
            <select
              id="lab-scorer"
              className={`${controlClass} w-full`}
              value={scoringTeam}
              onChange={(e) => setScoringTeam(e.target.value)}
            >
              {SCORING_TEAMS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.value}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-[13px] text-[#334155]">
          <input
            type="checkbox"
            checked={showStatistics}
            onChange={(e) => setShowStatistics(e.target.checked)}
          />
          Live match statistics
        </label>
        <label className="flex items-start gap-2 text-[13px] text-[#334155]">
          <input
            type="checkbox"
            className="mt-1"
            checked={forcePenaltyShootout}
            onChange={(e) => setForcePenaltyShootout(e.target.checked)}
          />
          <span>
            Straight to a penalty shootout
            <span className="block text-[12px] text-[#64748B]">
              No match clock: the jump controls do not apply to this game.
            </span>
          </span>
        </label>

        {error && <p className="text-[12px] text-[#B91C1C]">{error}</p>}
        <Btn variant="primary" disabled={!canCreate} onClick={create}>
          {busy ? 'Creating…' : 'Create mock game'}
        </Btn>
      </div>
    </Card>
  );
};

export default CreateGamePanel;
